import type { ChallengeInfo, ChatMessage, ClanChatMessage, PartyInvite, PartyState, QueueMode, RoomState, ServerLobbyMsg } from '@snakefeast/shared';
import { create } from 'zustand';
import i18n from '../i18n';
import { haptic } from '../lib/platform';
import { lobbySocket, type LobbyStatus } from '../lib/lobbySocket';
import { sfx } from '../lib/sound';
import { useToasts } from './toasts';

export interface PendingMatch {
  matchId: string;
  ticket: string;
  gameUrl: string;
  /** Watching someone else's match. */
  spectate?: boolean;
  /** Found by searching this queue (so "Play again" can search it again). */
  queueMode?: QueueMode;
}

interface LobbyState {
  connection: LobbyStatus;
  queue: { mode: QueueMode; since: number; typicalWaitMs: number | null } | null;
  room: RoomState | null;
  party: PartyState | null;
  partyInvites: PartyInvite[];
  /** Rematch votes for the match just played (cleared when a new match starts). */
  rematch: { matchId: string; ready: string[]; total: number } | null;
  challenges: ChallengeInfo[];
  outgoingChallenge: string | null;
  match: PendingMatch | null;
  presence: Record<string, 'online' | 'offline' | 'in_game'>;
  unread: Record<string, number>;
  /** Live messages per conversation (by other user id); history is fetched on open. */
  chat: Record<string, ChatMessage[]>;
  friendsVersion: number;
  /** Live clan chat since the clan screen loaded its history. */
  clanChat: ClanChatMessage[];
  clanUnread: number;
  /** Bumped when the server says something changed (clan membership, tournaments): screens refetch. */
  clanVersion: number;
  tournamentVersion: number;
  clearMatch: () => void;
  removeChallenge: (id: string) => void;
  removePartyInvite: (partyId: string) => void;
  markRead: (userId: string) => void;
}

export const useLobby = create<LobbyState>((set, get) => ({
  connection: 'closed',
  queue: null,
  room: null,
  party: null,
  partyInvites: [],
  rematch: null,
  challenges: [],
  outgoingChallenge: null,
  match: null,
  presence: {},
  unread: {},
  chat: {},
  friendsVersion: 0,
  clanChat: [],
  clanUnread: 0,
  clanVersion: 0,
  tournamentVersion: 0,
  clearMatch: () => set({ match: null }),
  removeChallenge: (id) => set({ challenges: get().challenges.filter((c) => c.id !== id) }),
  removePartyInvite: (partyId) => set({ partyInvites: get().partyInvites.filter((i) => i.partyId !== partyId) }),
  markRead: (userId) => set({ unread: { ...get().unread, [userId]: 0 } }),
}));

/** Routes every lobby push into state. Installed once at app start. */
/** True while the clan screen is open (no unread badge or toast then). */
export let clanChatOpen = false;
export const setClanChatOpen = (v: boolean) => {
  clanChatOpen = v;
  if (v) useLobby.setState({ clanUnread: 0 });
};

export function installLobbyHandlers(myId: () => string | undefined, activeChat: () => string | null): void {
  const t = i18n.t.bind(i18n);
  const toasts = useToasts.getState();
  lobbySocket.onStatus((connection) => useLobby.setState({ connection }));
  lobbySocket.on((msg: ServerLobbyMsg) => {
    const s = useLobby.getState();
    switch (msg.t) {
      case 'queue.status':
        useLobby.setState({ queue: msg.state === 'searching' && msg.mode ? { mode: msg.mode, since: msg.since ?? Date.now(), typicalWaitMs: msg.typicalWaitMs ?? null } : null });
        break;
      case 'match.found':
        sfx('matchFound');
        void haptic('success');
        useLobby.setState({ match: { matchId: msg.matchId, ticket: msg.ticket, gameUrl: msg.gameUrl, queueMode: s.queue?.mode }, queue: null, outgoingChallenge: null, rematch: null });
        break;
      case 'room.state':
        useLobby.setState({ room: msg.room });
        break;
      case 'room.closed':
        useLobby.setState({ room: null });
        if (msg.reason === 'kicked') toasts.push({ kind: 'error', text: t('room.kicked') });
        break;
      case 'party.state':
        useLobby.setState({ party: msg.party, partyInvites: s.partyInvites.filter((i) => i.partyId !== msg.party.id) });
        break;
      case 'party.closed':
        useLobby.setState({ party: null });
        if (msg.reason === 'kicked') toasts.push({ kind: 'error', text: t('party.kicked') });
        else if (msg.reason === 'disbanded') toasts.push({ kind: 'info', text: t('party.disbanded') });
        break;
      case 'party.invite':
        sfx('notify');
        void haptic('medium');
        useLobby.setState({ partyInvites: [...s.partyInvites.filter((i) => i.partyId !== msg.invite.partyId), msg.invite] });
        break;
      case 'rematch.status':
        if (msg.ready.length > (s.rematch?.matchId === msg.matchId ? s.rematch.ready.length : 0) && !msg.ready.includes(myId() ?? '')) sfx('notify');
        useLobby.setState({ rematch: { matchId: msg.matchId, ready: msg.ready, total: msg.total } });
        break;
      case 'challenge.incoming':
        sfx('notify');
        void haptic('medium');
        useLobby.setState({ challenges: [...s.challenges.filter((c) => c.id !== msg.challenge.id), msg.challenge] });
        break;
      case 'challenge.sent': {
        const id = msg.challenge.id;
        useLobby.setState({ outgoingChallenge: id });
        toasts.push({
          kind: 'info',
          text: t('challenge.sent'),
          ttl: Math.max(1000, msg.challenge.expiresAt - Date.now()),
          action: { label: t('challenge.cancel'), run: () => lobbySocket.send({ t: 'challenge.cancel', challengeId: id }) },
        });
        window.setTimeout(() => {
          if (useLobby.getState().outgoingChallenge === id) useLobby.setState({ outgoingChallenge: null });
        }, Math.max(0, msg.challenge.expiresAt - Date.now()));
        break;
      }
      case 'challenge.update':
        useLobby.setState({ challenges: s.challenges.filter((c) => c.id !== msg.challengeId) });
        if (s.outgoingChallenge === msg.challengeId) useLobby.setState({ outgoingChallenge: null });
        if (msg.status === 'declined') {
          toasts.push({
            kind: 'error',
            text: msg.blockedUntil ? t('challenge.blocked', { time: new Date(msg.blockedUntil).toLocaleString() }) : t('challenge.declined'),
            ttl: msg.blockedUntil ? 7000 : 4000,
          });
        }
        break;
      case 'friend.request':
        toasts.push({ kind: 'info', text: t('friends.requestFrom', { name: msg.from.displayName }) });
        useLobby.setState({ friendsVersion: s.friendsVersion + 1 });
        break;
      case 'friend.accepted':
        toasts.push({ kind: 'success', text: t('friends.acceptedBy', { name: msg.user.displayName }) });
        useLobby.setState({ friendsVersion: s.friendsVersion + 1 });
        break;
      case 'presence':
        useLobby.setState({ presence: { ...s.presence, [msg.userId]: msg.status } });
        break;
      case 'chat.message': {
        const me = myId();
        const other = msg.message.fromUserId === me ? msg.message.toUserId : msg.message.fromUserId;
        const list = s.chat[other] ?? [];
        // Replace the optimistic copy if this is the echo of our own message.
        const filtered = msg.message.clientId ? list.filter((m) => m.clientId !== msg.message.clientId) : list;
        if (filtered.some((m) => m.id === msg.message.id)) break;
        const chat = { ...s.chat, [other]: [...filtered, msg.message] };
        const unread = { ...s.unread };
        if (msg.message.fromUserId !== me && activeChat() !== other) {
          unread[other] = (unread[other] ?? 0) + 1;
          sfx('notify');
        }
        useLobby.setState({ chat, unread });
        break;
      }
      case 'clan.message': {
        if (s.clanChat.some((m) => m.id === msg.message.id)) break;
        const mine = msg.message.fromUserId === myId();
        useLobby.setState({ clanChat: [...s.clanChat.slice(-199), msg.message], clanUnread: clanChatOpen || mine ? s.clanUnread : s.clanUnread + 1 });
        if (msg.message.body.startsWith(':')) useLobby.setState({ clanVersion: useLobby.getState().clanVersion + 1 });
        else if (!mine && !clanChatOpen) sfx('notify');
        break;
      }
      case 'clan.kicked':
        toasts.push({ kind: 'error', text: t('clans.kicked') });
        useLobby.setState({ clanChat: [], clanUnread: 0, clanVersion: s.clanVersion + 1 });
        break;
      case 'tournament.update':
        useLobby.setState({ tournamentVersion: s.tournamentVersion + 1 });
        break;
      case 'error':
        if (msg.code !== 'rate_limited' || Math.random() < 0.3) {
          toasts.push({ kind: 'error', text: i18n.exists(`errors.${msg.code}`) ? t(`errors.${msg.code}`) : msg.message });
        }
        break;
      default:
        break;
    }
  });
}
